import { useState, useMemo, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useCachedQuery, useCachedMutations } from '@/hooks/useCachedQuery'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import Modal from '@/components/layout/Modal'
import type {
  PagoProgramado, Factura, Cliente, MovimientoCaja,
  FrecuenciaPago, FinSerie, AlcanceEliminacion,
} from '@/lib/types'
import {
  FRECUENCIAS_PAGO, FRECUENCIA_LABEL, USA_DIA_DEL_MES, USA_DIA_DE_SEMANA, DIAS_SEMANA_ISO,
} from '@/lib/types'
import { formatCurrency, formatDate } from '@/lib/utils'
import { IconArrowDown, IconArrowUp, IconCheck, IconEdit, IconPlus, IconRefresh, IconSearch, IconTrash } from '@/components/icons/Icons'
import { KpiRow, KpiStrip, PageHeader, Pagination, StatCard } from '@/components/ui'
import { usePagination } from '@/hooks/usePagination'

const emptyPago: Omit<PagoProgramado, 'id' | 'created_at'> = {
  tipo: 'egreso', concepto: '', descripcion: '', monto: 0, moneda: 'PEN',
  fecha_vencimiento: new Date().toISOString().slice(0, 10), estado: 'pendiente',
  factura_id: null, cliente_id: null, pago_recurrente_id: null, notas: null,
}

/* Repetición del alta. `fecha_inicio` no vive acá: es la fecha de vencimiento
   del propio pago, para no pedir dos veces la misma fecha. */
interface RepeticionForm {
  activa: boolean
  frecuencia: FrecuenciaPago
  dia_ejecucion: number | null
  fin: FinSerie
  fecha_fin: string
  repeticiones: number
}

const emptyRepeticion: RepeticionForm = {
  activa: false, frecuencia: 'mensual', dia_ejecucion: null,
  fin: 'nunca', fecha_fin: '', repeticiones: 12,
}

/** Lo que se adelanta cuando la serie no tiene fin declarado. */
const ADELANTO_SIN_FIN = 12

const estadoBadge: Record<string, string> = {
  pendiente: 'badge-borrador',
  pagado: 'badge-aceptada',
  vencido: 'badge-rechazada',
  reprogramado: 'badge-reprogramado',
}
const estadoLabel: Record<string, string> = {
  pendiente: 'Pendiente',
  pagado: 'Pagado',
  vencido: 'Vencido',
  reprogramado: 'Reprogramado',
}

const CRUMBS = [{ label: 'Contabilidad', to: '/contabilidad' }, { label: 'Pagos Programados' }]

export default function PagosProgramados() {
  const { data: items = [], isLoading: loading } = useCachedQuery<PagoProgramado>('pagos_programados')
  const { addMut, updateMut } = useCachedMutations('pagos_programados')
  const { addMut: addMovMut } = useCachedMutations('movimientos_caja')
  const { data: facturas = [] } = useCachedQuery<Factura>('facturas')
  const { data: clientes = [] } = useCachedQuery<Cliente>('clientes')
  const api = useApi()
  const qc = useQueryClient()

  const [modal, setModal] = useState<'create' | 'edit' | 'confirm' | 'reprogram' | null>(null)
  const [form, setForm] = useState(emptyPago)
  const [rep, setRep] = useState<RepeticionForm>(emptyRepeticion)
  const [preview, setPreview] = useState<string[]>([])
  const [guardando, setGuardando] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [del, setDel] = useState<PagoProgramado | null>(null)
  const [alcance, setAlcance] = useState<AlcanceEliminacion>('uno')
  const [cortarSerie, setCortarSerie] = useState(true)
  const [borrando, setBorrando] = useState(false)
  const [sel, setSel] = useState<Set<number>>(new Set())
  const [delMasivo, setDelMasivo] = useState(false)
  const [search, setSearch] = useState('')
  const [filtroEstado, setFiltroEstado] = useState<string>('todos')
  const [filtroTipo, setFiltroTipo] = useState<string>('todos')
  const [filtroMoneda, setFiltroMoneda] = useState<string>('todos')
  const [confirmTarget, setConfirmTarget] = useState<PagoProgramado | null>(null)
  const [reprogramDate, setReprogramDate] = useState('')

  const hoy = new Date().toISOString().slice(0, 10)

  const monedas = useMemo(
    () => [...new Set(items.map(p => p.moneda || 'PEN'))].sort(),
    [items],
  )

  const hayFiltros = !!search || filtroEstado !== 'todos' || filtroTipo !== 'todos' || filtroMoneda !== 'todos'
  const limpiarFiltros = () => { setSearch(''); setFiltroEstado('todos'); setFiltroTipo('todos'); setFiltroMoneda('todos') }

  /* ─── Filtros ─── */
  const filtered = useMemo(() => {
    let result = items.map(p => {
      // Auto-detect vencido visualmente
      if (p.estado === 'pendiente' && p.fecha_vencimiento < hoy) {
        return { ...p, _visual_estado: 'vencido' as const }
      }
      return { ...p, _visual_estado: p.estado }
    })
    if (filtroEstado !== 'todos') {
      result = result.filter(p => p._visual_estado === filtroEstado)
    }
    if (filtroTipo !== 'todos') {
      result = result.filter(p => p.tipo === filtroTipo)
    }
    if (filtroMoneda !== 'todos') {
      result = result.filter(p => (p.moneda || 'PEN') === filtroMoneda)
    }
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(p =>
        p.concepto?.toLowerCase().includes(q) ||
        p.descripcion?.toLowerCase().includes(q) ||
        p.notas?.toLowerCase().includes(q)
      )
    }
    // Ordenar solo por vencimiento dejaba los pagados viejos arriba y lo que
    // hay que pagar esta semana al fondo. Primero lo accionable.
    const rank = (e: string) => (e === 'vencido' ? 0 : e === 'pagado' ? 2 : 1)
    return result.sort((a, b) =>
      rank(a._visual_estado) - rank(b._visual_estado) ||
      a.fecha_vencimiento.localeCompare(b.fecha_vencimiento)
    )
  }, [items, filtroEstado, filtroTipo, filtroMoneda, search, hoy])

  const { page, setPage, totalPages, paginated, pageSize, total } =
    usePagination(filtered, 15, `${search}|${filtroEstado}|${filtroTipo}|${filtroMoneda}`)

  /* ─── Totales por moneda (solo lo pendiente; sumar S/ y $ juntos seria falso) ─── */
  const totales = useMemo(() => {
    const abiertos = items
      .map(p => ({ ...p, _v: p.estado === 'pendiente' && p.fecha_vencimiento < hoy ? 'vencido' : p.estado }))
      .filter(p => p._v !== 'pagado')
    const monedas = [...new Set(abiertos.map(p => p.moneda || 'PEN'))]
    return monedas.map(moneda => {
      const delMes = abiertos.filter(p => (p.moneda || 'PEN') === moneda)
      const suma = (f: (p: typeof delMes[number]) => boolean) =>
        delMes.filter(f).reduce((acc, p) => acc + Number(p.monto || 0), 0)
      return {
        moneda,
        vencido: suma(p => p._v === 'vencido' && p.tipo === 'egreso'),
        porPagar: suma(p => p._v !== 'vencido' && p.tipo === 'egreso'),
        porCobrar: suma(p => p.tipo === 'ingreso'),
      }
    }).filter(t => t.vencido || t.porPagar || t.porCobrar)
  }, [items, hoy])

  /* ─── Repetición ──────────────────────────────────────────────────────────
     Las fechas que se muestran acá las calcula el backend. Repetir la
     aritmética del calendario en el navegador es como nace el desfase entre lo
     que se previsualiza y lo que termina guardado. */
  const repeticionesPedidas =
    rep.fin === 'repeticiones' ? rep.repeticiones : ADELANTO_SIN_FIN

  // `useApi()` devuelve un objeto nuevo en cada render: como dependencia de un
  // efecto que hace setState, es un bucle infinito. Las funciones que devuelve
  // sí son estables, así que se depende de la función, no del objeto.
  const apiPost = api.post

  useEffect(() => {
    if (!rep.activa || !form.fecha_vencimiento) {
      setPreview(p => (p.length ? [] : p))
      return
    }
    let vigente = true
    const t = setTimeout(async () => {
      try {
        const r = await apiPost<{ fechas: string[] }>('/api/contabilidad/recurrencia/preview', {
          frecuencia: rep.frecuencia,
          fecha_inicio: form.fecha_vencimiento,
          dia_ejecucion: rep.dia_ejecucion,
          fecha_fin: rep.fin === 'fecha' ? rep.fecha_fin || null : null,
          repeticiones: repeticionesPedidas,
        })
        if (vigente) setPreview(r.fechas)
      } catch {
        if (vigente) setPreview([])
      }
    }, 250)
    return () => { vigente = false; clearTimeout(t) }
  }, [rep.activa, rep.frecuencia, rep.dia_ejecucion, rep.fin, rep.fecha_fin,
      repeticionesPedidas, form.fecha_vencimiento, apiPost])

  /* ─── CRUD ─── */
  const openCreate = () => {
    const hoyISO = new Date().toISOString().slice(0, 10)
    setForm({ ...emptyPago, fecha_vencimiento: hoyISO })
    setRep({ ...emptyRepeticion, dia_ejecucion: Number(hoyISO.slice(8, 10)) })
    setPreview([])
    setEditId(null)
    setModal('create')
  }
  const openEdit = (p: PagoProgramado) => {
    setRep(emptyRepeticion)
    setPreview([])
    setForm({
      tipo: p.tipo, concepto: p.concepto ?? '', descripcion: p.descripcion, monto: p.monto, moneda: p.moneda,
      fecha_vencimiento: p.fecha_vencimiento, estado: p.estado,
      factura_id: p.factura_id, cliente_id: p.cliente_id,
      pago_recurrente_id: p.pago_recurrente_id, notas: p.notas,
    })
    setEditId(p.id)
    setModal('edit')
  }

  const handleSave = async () => {
    if (!(form.concepto ?? '').trim()) { showToast('Concepto requerido', 'error'); return }
    if (form.monto <= 0) { showToast('Monto debe ser mayor a 0', 'error'); return }
    if (rep.activa && rep.fin === 'fecha' && !rep.fecha_fin) {
      showToast('Indica hasta qué fecha se repite', 'error'); return
    }
    setGuardando(true)
    try {
      if (modal === 'edit' && editId) {
        await updateMut.mutateAsync({ id: editId, ...form } as never)
        showToast('Pago actualizado', 'success')
      } else if (rep.activa) {
        // Una sola llamada crea la definición recurrente y todas sus
        // proyecciones, o no crea nada.
        const r = await api.post<{ creados: number }>('/api/contabilidad/pagos-programados/serie', {
          pago: form,
          serie: {
            frecuencia: rep.frecuencia,
            fecha_inicio: form.fecha_vencimiento,
            dia_ejecucion: rep.dia_ejecucion,
            fecha_fin: rep.fin === 'fecha' ? rep.fecha_fin || null : null,
            repeticiones: repeticionesPedidas,
          },
        })
        qc.invalidateQueries({ queryKey: ['pagos_programados'] })
        qc.invalidateQueries({ queryKey: ['pagos_recurrentes'] })
        showToast(`Serie creada: ${r.creados} vencimiento${r.creados !== 1 ? 's' : ''}`, 'success')
      } else {
        await addMut.mutateAsync(form as never)
        showToast('Pago programado', 'success')
      }
      setModal(null)
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error al guardar', 'error')
    } finally {
      setGuardando(false)
    }
  }

  /* ─── Eliminar ────────────────────────────────────────────────────────────
     Un pago de una serie no se borra solo: hay que poder cortar de esta fecha
     en adelante, o la serie entera. Lo ya confirmado nunca se toca — tiene un
     movimiento de caja detrás. */
  const hermanos = useMemo(() => {
    if (!del?.pago_recurrente_id) return []
    return items.filter(p => p.pago_recurrente_id === del.pago_recurrente_id)
  }, [items, del])

  const alcanceConteo = useMemo(() => {
    if (!del) return { uno: 0, siguientes: 0, serie: 0, pagados: 0 }
    const corte = del.fecha_vencimiento
    const borrables = hermanos.filter(p => p.estado !== 'pagado')
    return {
      uno: 1,
      siguientes: borrables.filter(p => p.fecha_vencimiento >= corte).length,
      serie: borrables.length,
      pagados: hermanos.filter(p => p.estado === 'pagado').length,
    }
  }, [del, hermanos])

  const openDelete = (p: PagoProgramado) => {
    setAlcance('uno')
    setCortarSerie(true)
    setDel(p)
  }

  const handleDelete = async () => {
    if (!del) return
    setBorrando(true)
    try {
      const r = await api.post<{ borrados: number; respetados: number }>(
        '/api/contabilidad/pagos-programados/eliminar',
        {
          id: del.id,
          alcance,
          desactivar_serie: alcance !== 'uno' && cortarSerie,
        },
      )
      qc.invalidateQueries({ queryKey: ['pagos_programados'] })
      qc.invalidateQueries({ queryKey: ['pagos_recurrentes'] })
      setSel(new Set())
      showToast(
        r.respetados > 0
          ? `${r.borrados} eliminados — ${r.respetados} ya pagados se conservaron`
          : `${r.borrados} pago${r.borrados !== 1 ? 's' : ''} eliminado${r.borrados !== 1 ? 's' : ''}`,
        'success',
      )
      setDel(null)
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error al eliminar', 'error')
    } finally {
      setBorrando(false)
    }
  }

  /* ─── Selección múltiple ─── */
  const toggleSel = (id: number) => {
    setSel(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const handleBulkDelete = async () => {
    setBorrando(true)
    try {
      const r = await api.post<{ borrados: number; respetados: number }>(
        '/api/contabilidad/pagos-programados/bulk-delete',
        { ids: [...sel] },
      )
      qc.invalidateQueries({ queryKey: ['pagos_programados'] })
      showToast(
        r.respetados > 0
          ? `${r.borrados} eliminados — ${r.respetados} ya pagados se conservaron`
          : `${r.borrados} pago${r.borrados !== 1 ? 's' : ''} eliminado${r.borrados !== 1 ? 's' : ''}`,
        'success',
      )
      setSel(new Set())
      setDelMasivo(false)
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error al eliminar', 'error')
    } finally {
      setBorrando(false)
    }
  }

  /* ─── Confirmar pago → crear movimiento ─── */
  const openConfirm = (p: PagoProgramado) => {
    setConfirmTarget(p)
    setModal('confirm')
  }
  const handleConfirmPago = async () => {
    if (!confirmTarget) return
    try {
      const movimiento: Omit<MovimientoCaja, 'id' | 'created_at'> = {
        tipo: confirmTarget.tipo,
        categoria: 'Pago programado',
        // El movimiento hereda el titulo del programado: sin esto, en Flujo de
        // Caja se pierde que fila era ("Multa SUNAT — IGV 06/2025").
        concepto: confirmTarget.concepto,
        descripcion: confirmTarget.descripcion,
        monto: confirmTarget.monto,
        moneda: confirmTarget.moneda,
        fecha: new Date().toISOString().slice(0, 10),
        factura_id: confirmTarget.factura_id,
        pago_programado_id: confirmTarget.id,
        cliente_id: confirmTarget.cliente_id,
        comprobantes: [],
        notas: `Pago confirmado desde programados (ID: ${confirmTarget.id})`,
        base_imponible: null, igv: null,
        serie_comprobante: null, numero_comprobante: null, tipo_comprobante: null,
        tiene_detraccion: false, porcentaje_detraccion: null, monto_detraccion: null,
      }
      await addMovMut.mutateAsync(movimiento as never)
      await updateMut.mutateAsync({ id: confirmTarget.id, estado: 'pagado' } as never)
      showToast('Pago confirmado y registrado en flujo de caja', 'success')
      setModal(null)
      setConfirmTarget(null)
    } catch {
      showToast('Error al confirmar pago', 'error')
    }
  }

  /* ─── Reprogramar ─── */
  const openReprogram = (p: PagoProgramado) => {
    setConfirmTarget(p)
    setReprogramDate(p.fecha_vencimiento)
    setModal('reprogram')
  }
  const handleReprogram = async () => {
    if (!confirmTarget || !reprogramDate) return
    try {
      await updateMut.mutateAsync({
        id: confirmTarget.id,
        fecha_vencimiento: reprogramDate,
        estado: 'pendiente',
      } as never)
      showToast('Pago reprogramado', 'success')
      setModal(null)
      setConfirmTarget(null)
    } catch {
      showToast('Error al reprogramar', 'error')
    }
  }

  if (loading) return (
    <div className="page-enter">
      <PageHeader breadcrumbs={CRUMBS} />
      <div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...
      </div>
    </div>
  )

  return (
    <div className="page-enter page-shell" id="pagos-programados">
      <PageHeader
        breadcrumbs={CRUMBS}
        meta={total === items.length
          ? `${items.length} pago${items.length !== 1 ? 's' : ''}`
          : `${total} de ${items.length}`}
        actions={
          <button className="btn btn-primary" onClick={openCreate} id="btn-new-pago-programado">
            <IconPlus size={16} /> Nuevo Pago
          </button>
        }
      />

      {/* Totales de lo que sigue abierto, por moneda. */}
      {totales.length > 0 && (
        <KpiStrip>
          {totales.map(t => (
            <KpiRow moneda={t.moneda} key={t.moneda}>
              {t.vencido > 0 && (
                <StatCard label="Vencido" value={formatCurrency(t.vencido, t.moneda)} tone="danger" />
              )}
              {t.porPagar > 0 && (
                <StatCard label="Por pagar" value={formatCurrency(t.porPagar, t.moneda)} tone="warning" />
              )}
              {t.porCobrar > 0 && (
                <StatCard label="Por cobrar" value={formatCurrency(t.porCobrar, t.moneda)} tone="success" />
              )}
            </KpiRow>
          ))}
        </KpiStrip>
      )}

      <div className="filters-bar">
        <div className="filters-bar__search">
          <IconSearch size={14} className="filters-bar__search-icon" />
          <input className="form-input" placeholder="Buscar por concepto, descripcion o nota..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-input" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{ width: 175 }}>
          <option value="todos">Todos los estados</option>
          <option value="pendiente">Pendientes</option>
          <option value="vencido">Vencidos</option>
          <option value="pagado">Pagados</option>
          <option value="reprogramado">Reprogramados</option>
        </select>
        <select className="form-input" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)} style={{ width: 150 }}>
          <option value="todos">Pagos y cobros</option>
          <option value="egreso">Solo pagos</option>
          <option value="ingreso">Solo cobros</option>
        </select>
        {monedas.length > 1 && (
          <select className="form-input" value={filtroMoneda} onChange={e => setFiltroMoneda(e.target.value)} style={{ width: 110 }}>
            <option value="todos">Moneda</option>
            {monedas.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
        {hayFiltros && (
          <button className="btn btn-sm btn-secondary filters-bar__clear" onClick={limpiarFiltros}>Limpiar filtros</button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="empty-state">
          <h3>Sin pagos programados</h3>
          <p>Programa tu primer pago futuro.</p>
          <button className="btn btn-primary" onClick={openCreate}>Programar Pago</button>
        </div>
      ) : (
        <>
        {sel.size > 0 && (
          <div className="bulk-bar">
            <span className="bulk-bar__count">
              {sel.size} seleccionado{sel.size !== 1 ? 's' : ''}
            </span>
            {sel.size < total && (
              <button className="btn btn-sm btn-secondary" onClick={() => setSel(new Set(filtered.map(p => p.id)))}>
                Seleccionar los {total} del filtro
              </button>
            )}
            <button className="btn btn-sm btn-secondary" onClick={() => setSel(new Set())}>
              Quitar selección
            </button>
            <button className="btn btn-sm btn-danger bulk-bar__action" onClick={() => setDelMasivo(true)}>
              <IconTrash size={13} /> Eliminar ({sel.size})
            </button>
          </div>
        )}

        <div className="table-container page-shell__table">
          <table>
            <thead>
              <tr>
                <th className="cell-select">
                  <input
                    type="checkbox"
                    aria-label="Seleccionar todos los de esta página"
                    checked={paginated.length > 0 && paginated.every(p => sel.has(p.id))}
                    onChange={e => {
                      const marcar = e.target.checked
                      setSel(prev => {
                        const next = new Set(prev)
                        paginated.forEach(p => marcar ? next.add(p.id) : next.delete(p.id))
                        return next
                      })
                    }}
                  />
                </th>
                <th>Vencimiento</th>
                <th>Tipo</th>
                <th>Descripcion</th>
                <th>Monto</th>
                <th>Estado</th>
                <th>Factura</th>
                <th>Cliente</th>
                <th style={{ width: 205 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    Sin resultados para el filtro aplicado
                  </td>
                </tr>
              ) : paginated.map(p => (
                <tr
                  key={p.id}
                  className={[
                    p.estado === 'pagado' ? 'row--dimmed' : '',
                    sel.has(p.id) ? 'row--selected' : '',
                  ].filter(Boolean).join(' ') || undefined}
                >
                  <td className="cell-select">
                    <input
                      type="checkbox"
                      aria-label={`Seleccionar ${p.concepto || p.descripcion || 'pago'}`}
                      checked={sel.has(p.id)}
                      onChange={() => toggleSel(p.id)}
                    />
                  </td>
                  <td className="cell-date">{formatDate(p.fecha_vencimiento)}</td>
                  <td>
                    <span className={`badge ${p.tipo === 'ingreso' ? 'badge-ingreso' : 'badge-egreso'}`}>
                      {p.tipo === 'ingreso' ? <IconArrowUp size={11} /> : <IconArrowDown size={11} />}
                      {p.tipo === 'ingreso' ? 'Cobro' : 'Pago'}
                    </span>
                  </td>
                  <td>
                    {/* `concepto` es lo que distingue una fila de otra (ej. "Multa
                        SUNAT - IGV 06/2025"); mostrar solo `descripcion` volvia
                        identicas a filas que no lo son. */}
                    <div className="cell-stack">
                      <span className="cell-stack__title">
                        {p.concepto || p.descripcion || '—'}
                        {p.pago_recurrente_id && (
                          <span className="badge badge-recurrente">Recurrente</span>
                        )}
                      </span>
                      {p.concepto && p.descripcion && p.descripcion !== p.concepto && (
                        <span className="cell-stack__sub">{p.descripcion}</span>
                      )}
                    </div>
                  </td>
                  <td className="cell-amount">{formatCurrency(p.monto, p.moneda)}</td>
                  <td>
                    <span className={`badge ${estadoBadge[p._visual_estado] || ''}`}>
                      {estadoLabel[p._visual_estado] || p.estado}
                    </span>
                  </td>
                  <td>
                    {p.factura_id
                      ? <Link to={`/facturas/${p.factura_id}`} className="cell-link">Ver</Link>
                      : '—'}
                  </td>
                  <td className="cell-soft">
                    {p.cliente_id ? clientes.find(c => c.id === p.cliente_id)?.empresa || '—' : '—'}
                  </td>
                  <td>
                    <div className="row-actions row-actions--nowrap">
                      {p.estado !== 'pagado' && (
                        <>
                          <button className="btn btn-sm btn-primary" onClick={() => openConfirm(p)} title="Confirmar pago">
                            <IconCheck size={13} /> Confirmar
                          </button>
                          <button className="btn btn-sm btn-secondary" onClick={() => openReprogram(p)} title="Reprogramar">
                            <IconRefresh size={13} />
                          </button>
                        </>
                      )}
                      <button className="btn btn-sm btn-secondary" onClick={() => openEdit(p)} title="Editar"><IconEdit size={13} /></button>
                      <button className="btn btn-sm btn-danger" onClick={() => openDelete(p)} title="Eliminar"><IconTrash size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize}
          onPage={setPage} noun="pagos" />
        </>
      )}

      {/* ─── Modal Crear/Editar ─── */}
      <Modal isOpen={modal === 'create' || modal === 'edit'} onClose={() => setModal(null)} title={modal === 'edit' ? 'Editar Pago' : 'Nuevo Pago Programado'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Tipo</label>
              <select className="form-input" value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value as 'ingreso' | 'egreso' }))}>
                <option value="egreso">Egreso (Pago)</option>
                <option value="ingreso">Ingreso (Cobro)</option>
              </select>
            </div>
            <div>
              <label className="form-label">Fecha de Vencimiento *</label>
              <input type="date" className="form-input" value={form.fecha_vencimiento} onChange={e => setForm(f => ({ ...f, fecha_vencimiento: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="form-label">Concepto *</label>
            <input className="form-input" value={form.concepto ?? ''} onChange={e => setForm(f => ({ ...f, concepto: e.target.value }))} placeholder="Ej: Multa SUNAT — IGV 06/2025, Alquiler oficina..." />
          </div>
          <div className="form-group">
            <label className="form-label">Descripción</label>
            <input className="form-input" value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} placeholder="Detalle opcional que se muestra bajo el concepto" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Monto *</label>
              <input type="number" className="form-input" value={form.monto || ''} onChange={e => setForm(f => ({ ...f, monto: parseFloat(e.target.value) || 0 }))} min="0" step="0.01" />
            </div>
            <div>
              <label className="form-label">Moneda</label>
              <select className="form-input" value={form.moneda} onChange={e => setForm(f => ({ ...f, moneda: e.target.value }))}>
                <option value="PEN">PEN</option><option value="USD">USD</option><option value="EUR">EUR</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Factura vinculada</label>
              <select className="form-input" value={form.factura_id ?? ''} onChange={e => setForm(f => ({ ...f, factura_id: e.target.value ? parseInt(e.target.value) : null }))}>
                <option value="">— Ninguna —</option>
                {facturas.map(f => <option key={f.id} value={f.id}>{f.numero} — {f.cliente_nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Cliente</label>
              <select className="form-input" value={form.cliente_id ?? ''} onChange={e => setForm(f => ({ ...f, cliente_id: e.target.value ? parseInt(e.target.value) : null }))}>
                <option value="">— Ninguno —</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.empresa}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="form-label">Notas</label>
            <textarea className="form-input" value={form.notas || ''} onChange={e => setForm(f => ({ ...f, notas: e.target.value || null }))} rows={2} />
          </div>

          {/* ─── Repetición ───────────────────────────────────────────────
              Solo en el alta: editar un vencimiento suelto no debería poder
              convertirlo en serie sin decir qué pasa con los hermanos. */}
          {modal === 'create' && (
            <div className="repeticion">
              <label className="repeticion__toggle">
                <input
                  type="checkbox"
                  checked={rep.activa}
                  onChange={e => setRep(r => ({ ...r, activa: e.target.checked }))}
                />
                <span>Se repite</span>
                <span className="repeticion__hint">
                  Crea la serie completa, no solo este vencimiento
                </span>
              </label>

              {rep.activa && (
                <div className="repeticion__body">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label className="form-label">Frecuencia</label>
                      <select
                        className="form-input"
                        value={rep.frecuencia}
                        onChange={e => {
                          const frecuencia = e.target.value as FrecuenciaPago
                          setRep(r => ({
                            ...r,
                            frecuencia,
                            // El día pedido cambia de significado según la
                            // frecuencia: arrastrar un "20" a una semanal daría
                            // un día de semana inexistente.
                            dia_ejecucion: USA_DIA_DEL_MES.includes(frecuencia)
                              ? Number(form.fecha_vencimiento.slice(8, 10))
                              : USA_DIA_DE_SEMANA.includes(frecuencia) ? 1 : null,
                          }))
                        }}
                      >
                        {FRECUENCIAS_PAGO.map(f => (
                          <option key={f} value={f}>{FRECUENCIA_LABEL[f]}</option>
                        ))}
                      </select>
                    </div>

                    {USA_DIA_DEL_MES.includes(rep.frecuencia) && (
                      <div>
                        <label className="form-label">Día del mes</label>
                        <input
                          type="number" className="form-input" min={1} max={31}
                          value={rep.dia_ejecucion ?? ''}
                          onChange={e => setRep(r => ({
                            ...r, dia_ejecucion: e.target.value ? parseInt(e.target.value) : null,
                          }))}
                        />
                        <span className="form-hint">
                          Si el mes no tiene ese día, vence el último
                        </span>
                      </div>
                    )}

                    {USA_DIA_DE_SEMANA.includes(rep.frecuencia) && (
                      <div>
                        <label className="form-label">Día de la semana</label>
                        <select
                          className="form-input"
                          value={rep.dia_ejecucion ?? 1}
                          onChange={e => setRep(r => ({ ...r, dia_ejecucion: parseInt(e.target.value) }))}
                        >
                          {DIAS_SEMANA_ISO.map(d => (
                            <option key={d.valor} value={d.valor}>{d.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label className="form-label">Hasta</label>
                      <select
                        className="form-input"
                        value={rep.fin}
                        onChange={e => setRep(r => ({ ...r, fin: e.target.value as FinSerie }))}
                      >
                        <option value="nunca">Sin fin definido</option>
                        <option value="fecha">En una fecha</option>
                        <option value="repeticiones">Después de N veces</option>
                      </select>
                    </div>
                    {rep.fin === 'fecha' && (
                      <div>
                        <label className="form-label">Último vencimiento</label>
                        <input
                          type="date" className="form-input" value={rep.fecha_fin}
                          onChange={e => setRep(r => ({ ...r, fecha_fin: e.target.value }))}
                        />
                      </div>
                    )}
                    {rep.fin === 'repeticiones' && (
                      <div>
                        <label className="form-label">Cantidad de pagos</label>
                        <input
                          type="number" className="form-input" min={1} max={120}
                          value={rep.repeticiones}
                          onChange={e => setRep(r => ({ ...r, repeticiones: parseInt(e.target.value) || 1 }))}
                        />
                      </div>
                    )}
                  </div>

                  {rep.fin === 'nunca' && (
                    <span className="form-hint">
                      Se adelantan {ADELANTO_SIN_FIN} vencimientos; después se generan más
                      desde Pagos Recurrentes.
                    </span>
                  )}

                  {preview.length > 0 && (
                    <div className="repeticion__preview">
                      <div className="repeticion__preview-title">
                        Se crearán {preview.length} vencimiento{preview.length !== 1 ? 's' : ''}:
                      </div>
                      <div className="repeticion__preview-list">
                        {preview.slice(0, 6).map(d => (
                          <span key={d} className="badge badge-recurrente">{formatDate(d)}</span>
                        ))}
                        {preview.length > 6 && (
                          <span className="repeticion__preview-more">
                            +{preview.length - 6} más, hasta {formatDate(preview[preview.length - 1])}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={guardando || addMut.isPending || updateMut.isPending}>
            {(guardando || addMut.isPending || updateMut.isPending)
              ? 'Guardando...'
              : rep.activa && modal === 'create'
                ? `Crear ${preview.length || ''} pago${preview.length !== 1 ? 's' : ''}`.replace('  ', ' ')
                : 'Guardar'}
          </button>
        </div>
      </Modal>

      {/* ─── Modal Confirmar Pago ─── */}
      <Modal isOpen={modal === 'confirm'} onClose={() => { setModal(null); setConfirmTarget(null) }} title="Confirmar Pago">
        {confirmTarget && (
          <div>
            <p>¿Confirmar que se realizó el pago?</p>
            <div style={{ background: 'var(--surface)', borderRadius: 8, padding: 12, margin: '12px 0' }}>
              <div><strong>{confirmTarget.descripcion}</strong></div>
              <div style={{ fontWeight: 700, fontSize: '1.1rem', marginTop: 4 }}>
                {formatCurrency(confirmTarget.monto, confirmTarget.moneda)}
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                Vencimiento: {formatDate(confirmTarget.fecha_vencimiento)}
              </div>
            </div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Se registrará un movimiento en el Flujo de Caja con la fecha de hoy.
            </p>
          </div>
        )}
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => { setModal(null); setConfirmTarget(null) }}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleConfirmPago} disabled={addMovMut.isPending || updateMut.isPending}>
            <IconCheck size={14} /> Confirmar Pago
          </button>
        </div>
      </Modal>

      {/* ─── Modal Reprogramar ─── */}
      <Modal isOpen={modal === 'reprogram'} onClose={() => { setModal(null); setConfirmTarget(null) }} title="Reprogramar Pago">
        {confirmTarget && (
          <div>
            <p>Reprogramar <strong>{confirmTarget.descripcion}</strong></p>
            <div style={{ marginTop: 12 }}>
              <label className="form-label">Nueva fecha de vencimiento</label>
              <input type="date" className="form-input" value={reprogramDate} onChange={e => setReprogramDate(e.target.value)} />
            </div>
          </div>
        )}
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => { setModal(null); setConfirmTarget(null) }}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleReprogram} disabled={updateMut.isPending}>
            Reprogramar
          </button>
        </div>
      </Modal>

      {/* ─── Modal Eliminar ──────────────────────────────────────────────
          Un pago de una serie no se borra solo: hay que poder cortar de esta
          fecha en adelante o la serie entera. Un pago suelto conserva la
          confirmación simple de siempre. */}
      <Modal isOpen={!!del} onClose={() => setDel(null)} title="Eliminar Pago">
        {del && (
          <>
            <p>
              <strong>{del.concepto || del.descripcion}</strong>
              {' — '}{formatCurrency(del.monto, del.moneda)}
              {' · '}{formatDate(del.fecha_vencimiento)}
            </p>

            {del.pago_recurrente_id ? (
              <>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                  Este pago es parte de una serie de {hermanos.length} vencimientos.
                  ¿Qué querés eliminar?
                </p>
                <div className="radio-group">
                  <label className={`radio-option ${alcance === 'uno' ? 'radio-option--active' : ''}`}>
                    <input type="radio" name="alcance" checked={alcance === 'uno'}
                      onChange={() => setAlcance('uno')} />
                    <span className="radio-option__body">
                      <span className="radio-option__title">Solo este pago</span>
                      <span className="radio-option__sub">
                        El resto de la serie queda como está
                      </span>
                    </span>
                  </label>
                  <label className={`radio-option ${alcance === 'siguientes' ? 'radio-option--active' : ''}`}>
                    <input type="radio" name="alcance" checked={alcance === 'siguientes'}
                      onChange={() => setAlcance('siguientes')} />
                    <span className="radio-option__body">
                      <span className="radio-option__title">
                        Este y los siguientes ({alcanceConteo.siguientes})
                      </span>
                      <span className="radio-option__sub">
                        Desde el {formatDate(del.fecha_vencimiento)} en adelante
                      </span>
                    </span>
                  </label>
                  <label className={`radio-option ${alcance === 'serie' ? 'radio-option--active' : ''}`}>
                    <input type="radio" name="alcance" checked={alcance === 'serie'}
                      onChange={() => setAlcance('serie')} />
                    <span className="radio-option__body">
                      <span className="radio-option__title">
                        Toda la serie ({alcanceConteo.serie})
                      </span>
                      <span className="radio-option__sub">
                        Incluye los vencimientos ya pasados que sigan pendientes
                      </span>
                    </span>
                  </label>
                </div>

                {alcance !== 'uno' && (
                  <label className="checkbox-inline">
                    <input type="checkbox" checked={cortarSerie}
                      onChange={e => setCortarSerie(e.target.checked)} />
                    <span>
                      Desactivar también la definición recurrente
                      <span className="form-hint">
                        Sin esto, el próximo &laquo;Generar&raquo; vuelve a crear lo que acabás de borrar
                      </span>
                    </span>
                  </label>
                )}

                {alcanceConteo.pagados > 0 && (
                  <p className="form-hint" style={{ marginTop: 10 }}>
                    {alcanceConteo.pagados} pago{alcanceConteo.pagados !== 1 ? 's' : ''} de esta serie
                    ya {alcanceConteo.pagados !== 1 ? 'están confirmados' : 'está confirmado'} y
                    no se {alcanceConteo.pagados !== 1 ? 'eliminan' : 'elimina'}: {alcanceConteo.pagados !== 1 ? 'tienen' : 'tiene'} un
                    movimiento de caja detrás.
                  </p>
                )}
              </>
            ) : (
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Es un pago suelto, no pertenece a ninguna serie.
              </p>
            )}
          </>
        )}
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setDel(null)}>Cancelar</button>
          <button className="btn btn-danger" onClick={handleDelete} disabled={borrando}>
            {borrando ? 'Eliminando...' : 'Eliminar'}
          </button>
        </div>
      </Modal>

      {/* ─── Modal Eliminar selección ─── */}
      <Modal isOpen={delMasivo} onClose={() => setDelMasivo(false)} title="Eliminar selección">
        <p>
          ¿Eliminar {sel.size} pago{sel.size !== 1 ? 's' : ''} programado{sel.size !== 1 ? 's' : ''}?
        </p>
        {(() => {
          const pagados = items.filter(p => sel.has(p.id) && p.estado === 'pagado').length
          return pagados > 0 ? (
            <p className="form-hint">
              {pagados} de {sel.size} ya {pagados !== 1 ? 'están confirmados' : 'está confirmado'} y
              se {pagados !== 1 ? 'conservan' : 'conserva'}: {pagados !== 1 ? 'tienen' : 'tiene'} un
              movimiento de caja detrás.
            </p>
          ) : null
        })()}
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setDelMasivo(false)}>Cancelar</button>
          <button className="btn btn-danger" onClick={handleBulkDelete} disabled={borrando}>
            {borrando ? 'Eliminando...' : `Eliminar (${sel.size})`}
          </button>
        </div>
      </Modal>
    </div>
  )
}
